import axios from "axios";
import { store } from "../redux/store";
import { logout, updateTokens } from "../redux/auth/authSlice";
import { showToast } from "../componants/toast/toastService";
import endpoints from "./endpoints";
import { getDeviceDetails } from "../utils/device";

const getServerErrorMessage = (data) => {
  if (!data) return null;

  // `error` (when present) is the backend's specific, field-level detail
  // (e.g. "Expected Guest Count: ... cannot exceed the venue's maximum
  // allowed (1000)."); `message` is often just a generic wrapper like
  // "Please check the details you entered and try again." — prefer the
  // specific one so validation errors are actually actionable in the toast.
  if (typeof data.error === "string" && data.error.trim()) {
    return data.error;
  }
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  if (data.error && typeof data.error === "object") {
    const nested = Object.values(data.error)
      .flat()
      .find((item) => typeof item === "string" && item.trim());
    if (nested) return nested;
  }

  const fieldErrors = Object.entries(data)
    .filter(([key]) => !["status", "statusCode", "message", "error", "data"].includes(key))
    .map(([key, value]) => {
      const msg = Array.isArray(value) ? value[0] : value;
      return msg ? `${key}: ${msg}` : null;
    })
    .filter(Boolean);

  return fieldErrors.length ? fieldErrors.join(", ") : null;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 120000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// ─── Refresh token queue ──────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

// ─── Request interceptor ──────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = store.getState()?.auth?.access_token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Device/locale headers the backend uses for session & device tracking.
    // Backend scopes Benzy ONLY on X-Device-ID (exact name; case-sensitive CORS).
    // Header's language switcher (redux `locale.language`) takes priority over
    // the browser's own locale once the user has picked one explicitly.
    const language = store.getState()?.locale?.language || (navigator.language || "en").split("-")[0];
    const deviceId = getDeviceDetails().device_id;
    if (deviceId) {
      config.headers["X-Device-ID"] = deviceId;
    }
    config.headers["X-Language"] = language;
    config.headers["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    config.headers["Accept-Language"] = language;
    // When sending FormData, remove Content-Type so axios sets it
    // automatically with the correct multipart boundary
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response interceptor ─────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => {
    const { silent } = response.config;
    if (!silent && response.data?.message) {
      showToast(response.data.message, "success");
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Network / no response from server
    if (!error.response) {
      const aborted = (
        error.code === "ERR_CANCELED"
        || error.name === "CanceledError"
        || error.message === "canceled"
      );
      if (!aborted && !originalRequest?.silent) {
        showToast("Network error. Please check your connection.", "error");
      }
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // ── 401: try refresh token, then retry ────────────────────────────────────
    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = store.getState()?.auth?.refresh_token;

      if (!refreshToken) {
        isRefreshing = false;
        processQueue(error, null);
        store.dispatch(logout());
        if (!originalRequest.silent) {
          showToast("Session expired. Please login again.", "error");
        }
        return Promise.reject(error);
      }

      try {
        const { data: refreshData } = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}${endpoints.users.refreshToken}`,
          { refresh: refreshToken }
        );

        // response shape: { data: { access, refresh? } }
        const newAccessToken = refreshData?.data?.access || refreshData?.access;
        const newRefreshToken = refreshData?.data?.refresh || refreshData?.refresh;

        store.dispatch(
          updateTokens({
            access_token: newAccessToken,
            ...(newRefreshToken && { refresh_token: newRefreshToken }),
          })
        );

        api.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch(logout());
        if (!originalRequest.silent) {
          showToast("Session expired. Please login again.", "error");
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ── Other HTTP errors ─────────────────────────────────────────────────────
    if (!originalRequest.silent) {
      const serverMessage = getServerErrorMessage(data);
      switch (status) {
        case 400:
          showToast(serverMessage || "Invalid request. Please check your input.", "error");
          break;
        case 403:
          showToast(serverMessage || "You don't have permission to perform this action.", "warning");
          break;
        case 404:
          showToast(serverMessage || "Requested resource not found.", "error");
          break;
        case 422:
          showToast(serverMessage || "Validation failed. Please check your input.", "warning");
          break;
        case 429:
          showToast("Too many requests. Please slow down.", "warning");
          break;
        case 500:
          showToast(serverMessage || "Server error. Please try again later.", "error");
          break;
        case 502:
        case 503:
        case 504:
          showToast("Service unavailable. Please try again later.", "error");
          break;
        default:
          showToast(serverMessage || "Something went wrong. Please try again.", "error");
      }
    }

    return Promise.reject(error);
  }
);

// ─── Service methods ──────────────────────────────────────────────────────────
const service = {
  get: (url, params = {}, config = {}) =>
    api.get(url, { params, ...config }),

  post: (url, data = {}, config = {}) =>
    api.post(url, data, config),

  put: (url, data = {}, config = {}) =>
    api.put(url, data, config),

  patch: (url, data = {}, config = {}) =>
    api.patch(url, data, config),

  delete: (url, config = {}) =>
    api.delete(url, config),

  upload: (url, data, config = {}) =>
    api.post(url, data, config),

  patchForm: (url, data, config = {}) =>
    api.patch(url, data, config),

  download: (url, config = {}) =>
    api.get(url, {
      ...config,
      responseType: "blob",
      headers: {
        // Drop axios default Accept/Content-Type; callers can override
        Accept: "*/*",
        "Content-Type": false,
        ...(config.headers || {}),
      },
    }),
};

export default service;
